import logger from 'utils/logger';

import { css } from '@emotion/css';
import { Color } from 'css/theme';
import { useRouter } from 'next/router';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { get } from 'api/queries';
import { customElementInteraction } from 'bindings/Segment.bs';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import { FormFields, toSelectOption } from 'models/giftReminders';
import Button, { Variations } from 'components/Button';
import FormCheckbox from 'components/FormCheckbox';
import FormDatePicker from 'components/FormDatePicker';
import FormInputError from 'components/FormInputError';
import { useQuery } from '@tanstack/react-query';
import { generateUniqueReminderId } from 'utils/reminder';

interface Props {
  onSubmit: (data: FormFields) => void;
  onCancel: () => void;
  isLoading: boolean;
  initialValues: FormFields;
  saveBtnText?: string;
  loadingBtnText?: string;
  cancelBtnText?: string;
  uid?: string;
  reminderInstanceId?: string;
}

const checkboxContainer = css({
  marginTop: '8px',
  display: 'flex',
  '>div+div': {
    marginLeft: '2.25rem',
  },
  marginBottom: '5px',
  '>legend': {
    marginBottom: '8px',
    fontWeight: 600,
  },
});

const fieldsetContainer = css({
  '> div+div': {
    marginTop: '16px',
  },
  ':disabled': {
    input: {
      color: Color.LightGray,
    },
  },
});
const formContainer = css({
  '> fieldset+fieldset': {
    marginTop: '16px',
  },
});

const activeText = css({
  color: Color.PrimaryBlue,
  marginTop: '5px !important',
});

const GiftReminderForm = ({
  onSubmit,
  initialValues,
  uid,
  onCancel,
  saveBtnText = 'Create reminder',
  loadingBtnText = 'Your reminder is being created...',
  cancelBtnText = 'No thanks',
  isLoading,
  reminderInstanceId = generateUniqueReminderId(),
}: Props) => {
  const router = useRouter();
  const { register, handleSubmit, control, errors, getValues, trigger, formState } = useForm<FormFields>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: initialValues,
    criteriaMode: 'firstError',
    shouldFocusError: true,
  });

  const {
    data: relationships,
    isError: isRelationshipError,
    isLoading: relationshipLoading,
    error: relationshipError,
  } = useQuery(
    ['relationships'],
    () => {
      return get('api/v3/reminders/relationships/', { withAuth: true }).then(data => {
        return data.map(toSelectOption);
      });
    },
    { staleTime: 1000 * 60 * 5 }
  );

  if (isRelationshipError) {
    logger.error(
      {
        err: relationshipError,
        source: 'Reminders API',
      },
      'error: unable to retrieve relationship data'
    );
  }

  const {
    data: occasions,
    isError: isOccasionError,
    isLoading: occasionLoading,
    error: occasionError,
  } = useQuery(
    ['occasions'],
    () => {
      return get('api/v3/reminders/occasions/', { withAuth: true }).then(data => {
        return data.map(toSelectOption);
      });
    },
    { staleTime: 1000 * 60 * 5 }
  );
  if (isOccasionError) {
    logger.error(
      {
        err: occasionError,
        source: 'Reminders API',
      },
      'error: unable to retrieve occasion data'
    );
  }

  const disabled = formState.isSubmitting;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={formContainer}>
      <fieldset className={fieldsetContainer}>
        <FormInput
          disabled={disabled}
          autoComplete="name"
          name="name"
          placeholder="Name of recipient"
          ref={register({
            required: 'Please enter a name',
          })}
          className="name-input"
          onBlur={() => {
            customElementInteraction({
              element: 'my_reminders_name',
              reminder_instance_id: reminderInstanceId,
              reminder_id: uid,
              path: router.pathname,
            });
          }}
          error={errors.name}
        />
        <Controller
          control={control}
          name="occasion"
          rules={{
            required: 'Please select an occasion',
          }}
          render={({ onChange, value, name }) => (
            <FormSelect
              disabled={disabled}
              placeholder="Occasion"
              name={name}
              onChange={onChange}
              onBlur={() => {
                customElementInteraction({
                  element: 'my_reminders_occasion',
                  reminder_instance_id: reminderInstanceId,
                  reminder_id: uid,
                  path: router.pathname,
                });
              }}
              value={value}
              error={errors[name]}
              options={occasionLoading ? [] : occasions}
            />
          )}
        />
        <Controller
          control={control}
          name="relationship"
          rules={{
            required: 'Please select a relationship',
          }}
          render={({ onChange, value, name }) => (
            <FormSelect
              disabled={disabled}
              placeholder="Relationship"
              name={name}
              onChange={onChange}
              onBlur={() => {
                customElementInteraction({
                  element: 'my_reminders_relationship',
                  reminder_instance_id: reminderInstanceId,
                  reminder_id: uid,
                  path: router.pathname,
                });
              }}
              value={value}
              error={errors[name]}
              options={relationshipLoading ? [] : relationships}
            />
          )}
        />
        <Controller
          control={control}
          name="date"
          rules={{
            required: 'Please select a date',
          }}
          render={({ onChange, value, name }) => (
            <FormDatePicker
              placeholder="Date of event"
              onChange={date => {
                customElementInteraction({
                  element: 'my_reminders_doe',
                  reminder_instance_id: reminderInstanceId,
                  reminder_id: uid,
                  path: router.pathname,
                });
                onChange(date);
              }}
              value={value}
              error={errors[name]}
              disabled={disabled}
              minDate={new Date()}
              renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                <>
                  <button
                    onClick={decreaseMonth}
                    type="button"
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                    aria-label="Previous Month"
                  >
                    Previous Month
                  </button>
                  <div className="react-datepicker__current-month">
                    {date.toLocaleString('default', { month: 'long' })}
                  </div>
                  <button
                    onClick={increaseMonth}
                    type="button"
                    className="react-datepicker__navigation react-datepicker__navigation--next"
                    aria-label="Next Month"
                  >
                    Next Month
                  </button>
                </>
              )}
              dateFormat="dd/MMM"
            />
          )}
        />
        <div className={activeText}>We’ll remind you 3 days before</div>
      </fieldset>
      <fieldset>
        <legend>Remind me via</legend>
        <div className={checkboxContainer}>
          <FormCheckbox
            ref={register({
              validate: data => {
                const sendViaSmsState = getValues().sendViaSms;
                return data || sendViaSmsState;
              },
            })}
            disabled={disabled}
            onClick={() => {
              trigger('sendViaSms');
            }}
            name="sendViaEmail"
          >
            Email
          </FormCheckbox>
          <FormCheckbox
            ref={register({
              validate: data => {
                const sendViaEmailState = getValues().sendViaEmail;
                return data || sendViaEmailState;
              },
            })}
            disabled={disabled}
            onClick={() => {
              trigger('sendViaEmail');
            }}
            name="sendViaSms"
          >
            SMS
          </FormCheckbox>
        </div>
        <FormInputError error={!!errors.sendViaEmail || !!errors.sendViaSms ? 'Selection required' : ''} />
      </fieldset>
      <fieldset className={fieldsetContainer}>
        <div>
          <Button
            disabled={!formState.isValid || !formState.isDirty || disabled}
            type="submit"
            variation={Variations.Primary}
            loading={isLoading}
          >
            {isLoading ? loadingBtnText : saveBtnText}
          </Button>
        </div>
        <div>
          <Button disabled={disabled} type="button" variation={Variations.Outlined} onClick={onCancel}>
            {cancelBtnText}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};

export default GiftReminderForm;
